// =====================================================
// DASHBOARD STATS — Résolution des éditions (N / N-1)
// =====================================================
// Une édition = un produit. N-1 = le produit de la MÊME ligne (nom sans année)
// dont l'année est immédiatement inférieure. Ex : « 4L Trophy 2027 » → N-1 =
// « 4L Trophy 2026 ». « Rose Trip 2027 » n'a pas de N-1 seedé → prevEditionId null.

import type { EditionRef } from './types';

export interface RawProduct {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
}

/** Extrait l'année (4 chiffres en fin de nom) et la ligne (nom sans année). */
export function parseEditionName(name: string): { line: string; year: number | null } {
  const m = name.match(/^(.*?)\s*(\d{4})\s*$/);
  if (m) {
    return { line: m[1].trim().toLowerCase(), year: Number(m[2]) };
  }
  return { line: name.trim().toLowerCase(), year: null };
}

/**
 * Construit la liste des éditions avec appariement N-1.
 * Trié par startDate décroissante (édition la plus récente d'abord).
 */
export function resolveEditions(products: RawProduct[]): EditionRef[] {
  const parsed = products.map((p) => {
    const { line, year } = parseEditionName(p.name);
    return { ...p, line, year };
  });

  const editions: EditionRef[] = parsed.map((p) => {
    // N-1 = même ligne, année = year-1 (le plus proche inférieur si year-1 absent).
    let prevEditionId: string | null = null;
    if (p.year != null) {
      const sameLine = parsed
        .filter((q) => q.line === p.line && q.year != null && q.year < p.year!)
        .sort((a, b) => b.year! - a.year!);
      prevEditionId = sameLine[0]?.id ?? null;
    }
    return {
      id: p.id,
      name: p.name,
      line: p.line,
      year: p.year,
      startDate: p.start_date,
      endDate: p.end_date,
      prevEditionId,
    };
  });

  return editions.sort((a, b) => {
    const ta = a.startDate ? new Date(a.startDate).getTime() : 0;
    const tb = b.startDate ? new Date(b.startDate).getTime() : 0;
    return tb - ta;
  });
}

/**
 * Sélectionne l'édition N par défaut : l'édition « active » = celle qui a le plus
 * de crews (proxy d'activité), à défaut la plus récente. `crewCountByProduct`
 * est optionnel ; sans lui, on prend la première (plus récente).
 */
export function pickDefaultEdition(
  editions: EditionRef[],
  crewCountByProduct?: Record<string, number>
): EditionRef | null {
  if (editions.length === 0) return null;
  if (crewCountByProduct) {
    const ranked = [...editions].sort(
      (a, b) => (crewCountByProduct[b.id] ?? 0) - (crewCountByProduct[a.id] ?? 0)
    );
    if ((crewCountByProduct[ranked[0].id] ?? 0) > 0) return ranked[0];
  }
  return editions[0];
}
